import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { useFeatureFlags } from '../context/FeatureFlagsContext';
import './Home.css';

const Home = () => {
  const navigate = useNavigate();
  const { flags } = useFeatureFlags();
  const heroRef = useRef(null);
  const heroContentRef = useRef(null);
  const mascotRef = useRef(null);

  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return;

    const pupils = document.querySelectorAll('.home-page .pupil');
    const eyes = document.querySelectorAll('.home-page .eye');
    const hero = heroRef.current;
    const content = heroContentRef.current;
    const mascot = mascotRef.current;

    const onPupils = (e) => {
      eyes.forEach((eye, i) => {
        const r = eye.getBoundingClientRect();
        const cx = r.left + r.width / 2;
        const cy = r.top + r.height / 2;
        const ang = Math.atan2(e.clientY - cy, e.clientX - cx);
        const dist = 9;
        if (pupils[i]) {
          pupils[i].style.transform =
            `translate(${Math.cos(ang) * dist}px, ${Math.sin(ang) * dist}px)`;
        }
      });
    };

    const onParallax = (e) => {
      if (!hero || !content || !mascot) return;
      const r = hero.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width - 0.5;
      const y = (e.clientY - r.top) / r.height - 0.5;
      content.style.transform = `rotateY(${x * 5}deg) rotateX(${-y * 4}deg)`;
      mascot.style.transform = `translate(${x * 22}px, ${y * 16}px)`;
    };

    const onLeave = () => {
      if (content) content.style.transform = '';
      if (mascot) mascot.style.transform = '';
    };

    window.addEventListener('mousemove', onPupils);
    if (hero) {
      hero.addEventListener('mousemove', onParallax);
      hero.addEventListener('mouseleave', onLeave);
    }

    return () => {
      window.removeEventListener('mousemove', onPupils);
      if (hero) {
        hero.removeEventListener('mousemove', onParallax);
        hero.removeEventListener('mouseleave', onLeave);
      }
    };
  }, []);

  return (
    <div className="home-page">
      <Header isLoggedIn={false} />

      <section className="hero" ref={heroRef}>
        <div className="sun"></div>
        <span className="cloud c1"></span>
        <span className="cloud c2"></span>
        <span className="cloud c3"></span>

        <span className="bit b1">A</span>
        <span className="bit b2">B</span>
        <span className="bit b3">★</span>
        <span className="bit b4">1</span>
        <span className="bit b5">C</span>

        <div className="hero-content" ref={heroContentRef}>
          <span className="eyebrow">🌟 A happy place to learn 🌟</span>
          <h1>Learning Made <span className="pop">FUN</span> for Special Children</h1>
          <p>Our interactive platform helps children with intellectual disabilities develop essential skills through engaging activities designed with their unique needs in mind.</p>
          <p>We focus on sight words, word scrambling, and categorization to build confidence and literacy skills in a supportive environment.</p>
          <div className="cta-buttons">
            <button className="cta-button" onClick={() => navigate('/login')}>
              <i className="fas fa-star"></i> Start Playing Now
            </button>
            {flags.marketing_video_enabled && (
              <button className="cta-button-outline" onClick={() => navigate('/demo')}>
                <i className="fas fa-play"></i> Watch the Demo
              </button>
            )}
          </div>
        </div>

        <div className="hero-image">
          <div className="mascot" ref={mascotRef}>
            <div className="shadow"></div>
            <div className="arm left"></div>
            <div className="arm right"></div>
            <div className="body"></div>
            <div className="eye left"><div className="pupil"></div></div>
            <div className="eye right"><div className="pupil"></div></div>
            <div className="lid"></div>
            <div className="cheek left"></div>
            <div className="cheek right"></div>
            <div className="smile"></div>
          </div>
        </div>

        <div className="hill">
          <span className="flower">🌼</span>
          <span className="flower">🌷</span>
          <span className="flower">🌹</span>
          <span className="flower">🌼</span>
        </div>
      </section>

      <section className="features">
        <h2 className="section-title">Pick a game and let's <span>play!</span></h2>
        <div className="feature-card fc1">
          <div className="icon-chip"><i className="fas fa-book-reader"></i></div>
          <h3>Sight Words Practice</h3>
          <p>Interactive exercises to help children recognize high-frequency words instantly, building reading fluency and confidence.</p>
        </div>
        <div className="feature-card fc2">
          <div className="icon-chip"><i className="fas fa-puzzle-piece"></i></div>
          <h3>Word Scrambling</h3>
          <p>Fun puzzles that challenge children to unscramble letters into meaningful words, improving spelling and cognitive skills.</p>
        </div>
        <div className="feature-card fc3">
          <div className="icon-chip"><i className="fas fa-tags"></i></div>
          <h3>Category Matching</h3>
          <p>Engaging activities that group words by themes, enhancing vocabulary and conceptual understanding.</p>
        </div>
      </section>
    </div>
  );
};

export default Home;
